import {Component, ElementRef, Inject, inject, OnDestroy, OnInit, signal, ViewChild} from '@angular/core';
import {CalendarDay} from "../../x-components/small-calendar/x-models/calendar-day";
import {CalendarEntry} from "../../x-components/small-calendar/x-models/calendar-entry";
import {EventHttpService} from "../../../management/x-http-requests/event-http.service";
import {EventNl} from "../../../models/event/event-nl";
import {Tuple} from "../../../models/_generic/tuple";
import {GlobalAlertService} from "../../../_services/global-alert.service";
import {AlertLevel} from "../../../enums/alert-level";
import {
  EmployeeEventResponseDto,
  EmployeeEventResponseDtoMinimal
} from "../../../models/event/employee-event-response-dto";
import {LocationNl} from "../../../models/location-nl";
import {ShiftStorage} from "../x-models/shift-storage";
import {ShiftLocation} from "../x-models/shift-location";
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDropList,
  moveItemInArray,
  transferArrayItem
} from "@angular/cdk/drag-drop";
import {EmployeeNl} from "../x-models/employee-nl";
import {Side} from "../x-models/x-enums/side";
import * as signalR from '@microsoft/signalr';
import {HubConnection, HubConnectionState} from '@microsoft/signalr';
import {AuthenticationService} from "../../../_auth/authentication.service";
import {ShiftMakerBatchResponse, ShiftMakerResponse} from "../x-models/shift-maker-response";
import {PlatformScannerService} from "../../../_services/platform-scanner.service";
import {Platform} from "../../../enums/platform";
import {filter, map, switchMap} from "rxjs";
import {CompanyTimeZoneService} from "../../../_shared/time/company-time-zone.service";
import {
  addCalendarDays,
  companyDayKey,
  companyLocalTimeInstant,
  companyToday,
  companyWallClock,
  DayKey,
  dayKeyToPickerDate,
  isDayKey,
  parseDateTimeLocalValue,
  pickerDateToDayKey,
  toDateTimeLocalValue
} from "../../../_shared/time/company-day";
import {AppRoutes} from "../../../enums/routes";
import {CompanyRoutes} from "../x-models/x-enums/company-routes";
import {DownloadInformation} from "../../x-components/x-models/download-information";
import {DateHelperService} from "../../x-components/x-services/date-helper.service";
import {ResponseState} from "../x-models/x-enums/response-state";
import {EventKind} from "../../../enums/event-kind";
import {InlineNoticeComponent} from "../../../_ui/components/feedback/inline-notice/inline-notice.component";
import {PermissionService} from "../../../_auth/permission.service";
import {LocationDto} from "../../../models/location-dto";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {EmployeeStatsDto, EmployeeStatsHelper} from "../x-models/employee-stats-dto";
import {ServerEndpoints} from "../../../server.endpoints";
import {LocationRoutes} from "../../location/_models/_enums/location-routes";
import {ShiftMakerSettings} from "../_models/settings/shift-maker-settings";
import {ShiftPlanHttpService} from "../_services/_http/shift-plan-http.service";
import {ShiftPlanDay, ShiftPlanScope} from "../_models/shift-plan/shift-plan";
import {CalculationType} from "../../../_models/_general/calculation-type";
import {
  EmployeeAttributePopupComponent
} from "../../x-components/employee-attribute-popup/employee-attribute-popup.component";
import {FormsModule} from "@angular/forms";
import {CommonModule, CurrencyPipe, DatePipe, DecimalPipe} from "@angular/common";
import {DownloadComponent} from "../../x-components/download/download.component";
import {SmallCalendarComponent} from "../../x-components/small-calendar/small-calendar.component";
import {NgOptionComponent, NgSelectComponent} from "@ng-select/ng-select";
import {NgSelectScrollAwareDirective} from "../../../_directives/ng-select-scroll-aware.directive";
import {ContactChannelChipComponent} from "../../../_ui/components/contact-chip/contact-chip.component";
import {ContactQuickLinkHttpService} from "../contact-quick-links/contact-quick-link-http.service";
import {ContactChannelQuickLink} from "../contact-quick-links/contact-quick-link.models";
import {contactChannelLabel} from "../employees/contact-requirements/contact-channels";

@Component({
  selector: 'app-shift-maker',
  templateUrl: './shift-maker.component.html',
  imports: [
    EmployeeAttributePopupComponent,
    RouterLink,
    FormsModule,
    CommonModule,
    DownloadComponent,
    SmallCalendarComponent,
    CdkDropList,
    CdkDrag,
    CdkDragPlaceholder,
    NgSelectComponent,
    NgOptionComponent,
    // #236: this component has had `[appendTo]` since long before the bug was filed, and until now NO scroll
    // handling at all — a portaled panel simply stayed nailed to the page while the board scrolled under it.
    // The directive's selector is `ng-select[appendTo]`, so importing it here is the whole wire-up; dropping
    // the import silently reintroduces that.
    NgSelectScrollAwareDirective,
    DecimalPipe,
    CurrencyPipe,
    DatePipe,
    ContactChannelChipComponent,
    InlineNoticeComponent
  ],
  styleUrls: ['./shift-maker.component.scss']
})
export class ShiftMakerComponent implements OnInit, OnDestroy {
  @ViewChild("employeeHeader") employeeHeader!: ElementRef<HTMLSpanElement>;
  @ViewChild("employeeModal") employeeModal!: ElementRef<HTMLDivElement>;
  downloadInformation: DownloadInformation = new DownloadInformation();
  downloadParams: string = "";
  downloadExtended: boolean = false;
  downloadPng: boolean = false;
  /**
   * The shift-plan export range: company calendar days (`yyyy-MM-dd`), both inclusive, sent verbatim. The server
   * resolves them in the company zone (#650); the old browser-midnight instants plus a server-side `+24h` were off
   * by an hour across a DST change and depended on the viewer's zone.
   */
  downloadStart: DayKey = '';
  downloadEnd: DayKey = '';
  private readonly companyTimeZone = inject(CompanyTimeZoneService);
  /**
   * Today as a COMPANY calendar day, set once the zone has loaded. Until then the month picker is not rendered, so
   * nothing (disabled locations, availability, roster, export window) is ever loaded for the BROWSER's today first:
   * at 01:00 in Tokyo the browser already says 02.08. while a Berlin company is still on 01.08.
   */
  protected readonly todayKey = signal<DayKey | undefined>(undefined);
  events: EventNl[] = [];
  /**
   * The day's events that the board is NOT drawing, because they carry „Im Dienstplan ausblenden".
   *
   * Kept as the payload's own list rather than derived from {@link events}: the board's list has already had
   * them removed server-side, so there is nothing here to derive from. Reset with everything else in
   * {@link applyDay}.
   */
  hiddenEvents: EventNl[] = [];
  filteredEvents: EventNl[] = [];
  showingEvent: EventNl | undefined;
  shifts: ShiftStorage[] = [];
  responses: EmployeeEventResponseDto[] = [];
  responseList: any = {};
  companyLocations: any = {};
  /**
   * How much of the plan the last day load actually returned (#409).
   *
   * An administering role gets `bounded: false` and the whole day; a `ShiftLead` gets the single event they may
   * act on. Kept on the component because a narrowed board and an empty day are indistinguishable on screen,
   * and the difference is the first thing a shift lead needs to know before concluding the screen is broken.
   */
  scope: ShiftPlanScope = new ShiftPlanScope();
  selectedDay: CalendarDay = new CalendarDay(new Date(), false);
  calendarEntries: CalendarEntry[] = [];
  calendarRange: Tuple<Date, Date> = new Tuple<Date, Date>();
  edit: boolean = false;
  nextSelectedDate: CalendarDay | undefined;
  nextSelectedEvent: string | undefined;
  /**
   * The raw `?date=` query parameter, if the board was opened through a deep link (#664).
   *
   * Kept raw because it arrives before the company zone does, and resolving it needs the zone: the callers
   * (event details, the virtual-event tile) send the event's START INSTANT, and the day that instant falls on
   * is a company-zone question — an event starting 01.08. 01:00 Berlin is a 31.07. instant in UTC.
   */
  private deepLinkDate: string | undefined;
  /** Set once the first `queryParams` emission has been read, so the deep link is consumed on entry only (#664). */
  private deepLinkConsumed: boolean = false;
  public Connections: any = {};

  /**
   * The in-flight `stop()` of each connection, keyed exactly like {@link Connections}.
   *
   * `HubConnection.stop()` moves the connection to `Disconnecting` synchronously and only settles a turn
   * later, and `start()` throws while it is in that state. The day switch tears down and reconnects in one
   * synchronous pass, so without this the reconnect either silently no-ops or throws — see
   * {@link stopConnection}.
   */
  private connectionStops: {[eventId: string]: Promise<void>} = {};
  /** The hub method for a single push — `PushGeneralUpdate` or `PushLiveUpdate`, by permission. */
  endpoint: string = "";
  /**
   * The hub method for a batched re-index — `PushGeneralIndexUpdates` or `PushLiveIndexUpdates`. The batch
   * endpoints mint no permission id of their own; they are gated by the same permission as their single-push
   * counterpart, so this always tracks {@link endpoint}.
   */
  batchEndpoint: string = "";
  height: number = 10;
  employees: any = {};
  isUpdating: boolean = false;
  employeeList: EmployeeNl[] = [];
  mouseEvent?: MouseEvent;
  visible: boolean = false;
  eVisible: any = {};
  popupEmployee: EmployeeNl | undefined;
  redirectPath: any = {};
  queryParams: any = {};
  /**
   * Stable empty stand-ins for drop containers this window has not loaded, keyed `{eventId}_{locationId}`.
   * See {@link getShiftLocationResponses} for why they must be cached rather than created per read.
   */
  private unknownContainers: {[key: string]: EmployeeEventResponseDto[]} = {};
  /** Set while applying a payload that named a container this window does not know (see {@link flushUnknownContainerResync}). */
  private unknownContainerSeen: boolean = false;
  /** Epoch ms of the last self-heal reload, `0` when none has run. */
  private lastResyncAt: number = 0;
  /** Self-heal reloads in the current streak; reset once the board goes quiet for {@link resyncStreakResetMs}. */
  private consecutiveResyncs: number = 0;

  /*
   * Why the self-heal below is rate-limited at all — do NOT simplify this away.
   *
   * A broadcast naming an unloaded container means this window's location set is stale, so it reloads the
   * day (`selectedDateChangedWrapper(true)` — two HTTP calls plus a full board rebuild). That is the right
   * repair, but the trigger is attacker-free yet self-amplifying in three ways: ONE batched `ReceiveChanges`
   * can name the same unknown container a dozen times; every broadcast that follows names it again; and
   * every window watching the event reacts to the same message. Unbounded, a single permanently-unknown
   * container turns each edit anyone makes into a reload storm proportional to (open windows × cards).
   *
   * The three limits are therefore all load-bearing:
   *  - one resync per received payload, so a batch costs one reload, not one per item;
   *  - a minimum interval, so a stream of broadcasts cannot exceed two reloads per minute per window. 30 s is
   *    far above the day-load's cost and far above any human editing cadence, so a genuine location change
   *    still heals on the very next message;
   *  - a cap on consecutive attempts, because a container that is STILL unknown after three full reloads is
   *    not a staleness problem — it is a structural disagreement (a location outside this user's shard
   *    selection, or one the day-load endpoint does not return). Reloading a fourth time cannot fix it, so
   *    the feature degrades to "we told you, please reload" instead of looping forever.
   * The streak resets after a long quiet spell: a fresh occurrence half an hour later is a new incident, not
   * a continuation of an old loop.
   */
  /** Minimum wall-clock gap between two self-heal reloads. */
  private static readonly resyncMinIntervalMs: number = 30_000;
  /** How many self-heal reloads may run back-to-back before the component degrades to warn-only. */
  private static readonly maxConsecutiveResyncs: number = 3;
  /** Quiet period after which the consecutive-resync streak is considered broken. */
  private static readonly resyncStreakResetMs: number = 300_000;

  /**
   * `ApiError.code` → German UI copy for every refusal the shift plan can produce (the nine
   * `E_SHIFTMAKER_*` hub refusals from #386/#392, plus the three `E_SHIFT_LEAD_*`/`E_SHIFT_PLAN_*`
   * codes the #409 read gate can answer with).
   *
   * The server's own `ApiError.description` is developer-facing English **by design** — it exists for
   * Postman, logs and docs, the same as every other `ErrorCatalog` entry — so it must never reach this
   * screen directly (that was the bug: #386/#392 shipped nine new codes and this screen rendered their
   * English descriptions straight through, in an otherwise fully German UI). Read the mapped code's own
   * XML doc in `API.Core/Models/Company/ShiftMakerErrorCodes.cs` / `API.Core/Authorization/AuthErrorCodes.cs`
   * before touching a sentence here — the catalog description is a one-line summary, the doc comment above
   * it is what the sentence actually needs to say.
   *
   * @see messageFor for the (deliberately generic, never-`description`) fallback when a code is not here.
   */
  private static readonly errorMessages: Readonly<Record<string, string>> = {
    // ----- E_SHIFTMAKER_* (ShiftMakerHub, #386/#392) -----
    E_SHIFTMAKER_RESPONSE_NOT_FOUND:
      'Diese Verfügbarkeit wurde nicht gefunden – vermutlich wurde sie zwischenzeitlich gelöscht. Bitte ' +
      'laden Sie den Dienstplan neu.',
    E_SHIFTMAKER_RESPONSE_CONFLICT:
      'Dieser Eintrag wurde bereits angelegt. Bitte laden Sie den Dienstplan neu, um den aktuellen Stand ' +
      'zu sehen.',
    E_SHIFTMAKER_EMPLOYEE_IMMUTABLE:
      'Diese Verfügbarkeit wurde vom Mitarbeiter selbst eingetragen und kann nicht umbesetzt werden. Nur ' +
      'manuell angelegte Schichten lassen sich einem anderen Mitarbeiter zuweisen.',
    E_SHIFTMAKER_EMPLOYEE_NOT_EMPLOYED:
      'Dieser Mitarbeiter hat an diesem Tag kein Arbeitsverhältnis und kann daher nicht eingeplant werden.',
    E_SHIFTMAKER_EVENT_NOT_FOUND:
      'Die zugehörige Veranstaltung wurde nicht gefunden. Bitte laden Sie den Dienstplan neu.',
    E_SHIFTMAKER_EVENT_NOT_LIVE:
      'Änderungen sind nur möglich, solange die Veranstaltung läuft. Diese Veranstaltung ist derzeit nicht ' +
      'aktiv.',
    E_SHIFTMAKER_TIMEFRAME_UNAVAILABLE:
      'Für diesen Zeitraum ist kein Abrechnungszeitraum hinterlegt, die Mitarbeiterstatistik kann daher ' +
      'nicht berechnet werden. Bitte wenden Sie sich an die Administration.',
    E_SHIFTMAKER_BATCH_EMPTY:
      'Es wurden keine Änderungen zum Speichern übermittelt.',
    E_SHIFTMAKER_BATCH_TOO_LARGE:
      'Es wurden zu viele Änderungen auf einmal übermittelt. Bitte laden Sie die Seite neu und sortieren ' +
      'Sie in kleineren Schritten.',
    // ----- E_SHIFT_LEAD_* / E_SHIFT_PLAN_* (ShiftPlanController read gate, #409) -----
    E_SHIFT_LEAD_OUTSIDE_WINDOW:
      'Als Schichtleitung können Sie nur den Dienstplan der aktuell laufenden Veranstaltung bearbeiten.',
    E_SHIFT_LEAD_NO_CURRENT_EVENT:
      'Derzeit läuft keine Veranstaltung – als Schichtleitung gibt es daher aktuell nichts zu bearbeiten.',
    E_SHIFT_PLAN_NOT_PERMITTED:
      'Für dieses Konto ist der Dienstplan nicht freigegeben.',
  };

  /**
   * Generic German refusal copy for any code {@link errorMessages} does not (yet) know.
   *
   * Deliberately generic rather than falling back to `description`: a future backend code that ships
   * without a German mapping must degrade to THIS sentence, not to leaking English — see the class doc on
   * {@link errorMessages}. If you land here from a real refusal, the fix is to add that code's mapping
   * above, never to make this string more specific or to read `description` again.
   */
  private static readonly genericRefusal =
    'Der Vorgang konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut oder laden Sie die Seite neu.';

  /**
   * Maps a structured `ApiError.code` to its German UI sentence, or `fallback` when the code is absent —
   * `undefined` means the failure carried no structured `ApiError` at all (a raw network/5xx failure),
   * which is a different, call-site-specific situation from "we got a code but haven't mapped it yet"
   * (that always degrades to {@link genericRefusal}, never `fallback`).
   */
  private static messageFor(code: string | undefined, fallback: string = ShiftMakerComponent.genericRefusal): string {
    if (code == undefined) return fallback;
    return ShiftMakerComponent.errorMessages[code] ?? ShiftMakerComponent.genericRefusal;
  }

  protected now = new Date();
  protected settings: ShiftMakerSettings = new ShiftMakerSettings();
  protected readonly Side = Side;
  protected readonly Object = Object;
  protected readonly Platform = Platform;
  protected readonly HubConnectionState = HubConnectionState;
  protected readonly filter = filter;
  protected readonly Routes = AppRoutes;
  protected readonly CompanyRoutes = CompanyRoutes;
  protected readonly ResponseState = ResponseState;
  /** #664 — the event column chips a virtual event; the template needs the enum to say which. */
  protected readonly EventKind = EventKind;
  protected readonly location = location;
  protected readonly JSON = JSON;
  protected readonly ServerEndpoints = ServerEndpoints;
  protected readonly LocationRoutes = LocationRoutes;
  protected readonly CalculationType = CalculationType;
  protected readonly EmployeeStatsHelper = EmployeeStatsHelper;

  /**
   * The one backend door this screen reads through (#409).
   *
   * `eventHttpService` survives beside it for exactly one call — {@link lock}'s `changePublicResponseTime`,
   * which is an *event administration* write and not a shift-plan one (see {@link lock}).
   */
  constructor(private shiftPlanHttpService: ShiftPlanHttpService, private eventHttpService: EventHttpService,
              private globalAlertService: GlobalAlertService, public authenticationService: AuthenticationService,
              public platformScannerService: PlatformScannerService,
              public PermissionService: PermissionService, private router: Router, private route: ActivatedRoute,
              @Inject("BASE_URL") public baseUrl: string, private dateHelperService: DateHelperService,
              private contactQuickLinkHttpService: ContactQuickLinkHttpService) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.canUseContactQuickLinks = this.PermissionService.CanUseContactQuickLinks();
  }

  // ----- contact quick links (#558) -----

  /**
   * Whether this session may see the contact chips on the board — the four roles
   * `GET /company/contact-link/for-employees` gates on, `ShiftLead` included.
   *
   * A Schichtleitung is the reason this screen carries chips at all. They hold no employee-detail permission,
   * so „Profil anzeigen" is hidden for them and the board is the ONLY place they can see who is on tonight —
   * which, before #558, meant the only way to reach a late colleague was to ask somebody with more rights.
   */
  readonly canUseContactQuickLinks: boolean;

  /** Consented, openable channels per employee id, for the day currently on the board. */
  contactQuickLinks: ReadonlyMap<string, ContactChannelQuickLink[]> = new Map();

  protected readonly contactChannelLabel = contactChannelLabel;

  /** One employee's chips — `[]` while the board is still loading them, and `[]` when there are none. */
  contactLinksFor(employeeId: string | null | undefined): ContactChannelQuickLink[] {
    if (!employeeId) return [];
    return this.contactQuickLinks.get(employeeId) ?? [];
  }

  /**
   * Loads the chips for everybody on the board, in ONE batched call per day load.
   *
   * Driven from {@link applyDay} rather than per card: the same person appears in the pool and on every event
   * they are assigned to, so a per-card fetch would ask for the busiest employee of the evening half a dozen
   * times. `this.employees` is already the board's de-duplicated employee index, which makes it exactly the
   * right input — and the service caches per id on top, so re-selecting yesterday costs nothing.
   */
  private loadContactQuickLinks(): void {
    if (!this.canUseContactQuickLinks) return;
    const ids = Object.keys(this.employees ?? {});
    if (ids.length === 0) return;

    this.contactQuickLinkHttpService.forEmployees(ids).subscribe({
      next: links => this.contactQuickLinks = links,
      // Deliberately quiet. The board already raises a banner for a day that failed to load and another for
      // cards it could not place; a third for "the contact chips are missing" would push those off the one
      // banner GlobalAlertService keeps, to report the least consequential of the three.
      error: err => console.warn('[shift-maker] contact quick links unavailable', err)
    });
  }

  _large: boolean = false;

  get large(): boolean {
    return this._large;
  }

  set large(value: boolean) {
    this._large = value;
    this.assignEvents(this._large);
  }

  canEditPredicate(event: EventNl, permissionService: PermissionService) {
    return function (drag: CdkDrag, drop: CdkDropList) {
      if (permissionService.CheckPermission(permissionService.Hubs_ShiftMakerHub_PushGeneralUpdate())) return true;
      return permissionService.CheckPermission(permissionService.Hubs_ShiftMakerHub_PushLiveUpdate()) &&
        event.start <= new Date() && event.end > new Date();
    }
  }

  canEdit(event: EventNl | undefined = undefined): boolean {
    if (this.PermissionService.CheckPermission(this.PermissionService.Hubs_ShiftMakerHub_PushGeneralUpdate())) return true;
    if (!event) return this.PermissionService.CheckPermission(this.PermissionService.Hubs_ShiftMakerHub_PushLiveUpdate()) &&
      this.filteredEvents.findIndex(x => x.start <= new Date() && x.end > new Date()) > -1
    return this.PermissionService.CheckPermission(this.PermissionService.Hubs_ShiftMakerHub_PushLiveUpdate()) &&
      event.start <= new Date() && event.end > new Date();
  }

  ngOnInit(): void {
    let width = window.innerWidth;
    if (width >= 1536 && !this.large) {
      this.large = true;
    } else if (width < 1536 && this.large) {
      this.large = false;
    }

    this.route.queryParams.subscribe(params => {
      if (params["event"]) {
        this.nextSelectedEvent = params["event"]
      }
      // #664: `?date=` is read HERE and nowhere else — the month picker no longer touches the route at all,
      // because resolving the linked INSTANT needs the company's zone, which the picker has no way of knowing.
      //
      // It is also consumed exactly ONCE, on entry. A deep link says which day to LAND on; it must never
      // out-vote the day the user then picks, and the parameter stays in the URL for the whole visit, so
      // re-reading it on a later emission would silently re-impose the linked day.
      if (!this.deepLinkConsumed) {
        this.deepLinkConsumed = true;
        if (params["date"]) this.deepLinkDate = params["date"];
      }
    })


    // The initial selected day is the company's today (#656), unless a `?date=` deep link named another one.
    // The picker mounts only after this, and its init emit is what loads the day, so the first load already
    // asks for the right company day. `todayKey` stays TODAY either way — it is the "today" highlight, not the
    // selection.
    this.companyTimeZone.load().subscribe(timeZone => {
      const today = companyToday(timeZone);
      const day = this.deepLinkDayKey(timeZone) ?? today;
      this.selectedDay = new CalendarDay(dayKeyToPickerDate(day), false);
      this.setDownloadTimespan(day);
      this.todayKey.set(today);
    });

    if (this.PermissionService.CheckPermission(PermissionService.hubs_shiftmakerhub_pushgeneralupdate)) {
      this.endpoint = "PushGeneralUpdate";
      this.batchEndpoint = "PushGeneralIndexUpdates";
    } else if (this.PermissionService.CheckPermission(PermissionService.hubs_shiftmakerhub_pushliveupdate)) {
      this.endpoint = "PushLiveUpdate";
      this.batchEndpoint = "PushLiveIndexUpdates";
    }

    this.edit = !this.platformScannerService.isMobile()

    // The shift-maker settings used to be fetched here on their own; they now ride the day payload (#409), and
    // the day load is triggered by `app-small-calendar`'s own `ngOnInit` → `createDayArray` → `select()`.
  }

  setDownloadParams() {
    this.downloadParams = "start=" + this.downloadStart + "&end=" + this.downloadEnd + "&extended=" + this.downloadExtended + "&png=" + this.downloadPng;
  }

  /** A seven-day export window starting on `day`: `[day, day + 6]`, calendar arithmetic only (no `+ 6 * 24h`). */
  setDownloadTimespan(day: DayKey) {
    this.downloadStart = day;
    this.downloadEnd = addCalendarDays(day, 6);
    this.setDownloadParams();
  }

  setDownloadExtended(extended: boolean) {
    this.downloadExtended = extended;
    this.setDownloadParams();
  }

  setDownloadPng(png: boolean) {
    this.downloadPng = png;
    this.setDownloadParams();
  }

  /** The picked day, kept as the calendar day it is. A cleared input keeps the previous day. */
  setDownloadStart(start: string) {
    if (!isDayKey(start)) return;
    this.downloadStart = start;
    this.setDownloadParams();
  }

  setDownloadEnd(end: string) {
    if (!isDayKey(end)) return;
    this.downloadEnd = end;
    this.setDownloadParams();
  }

  ngOnDestroy(): void {
    this.stopAllConnections();
  }

  /**
   * Stops one hub connection and remembers the pending promise, so a reconnect issued in the same
   * synchronous pass can wait for it instead of racing it.
   */
  private stopConnection(eventId: string): void {
    this.connectionStops[eventId] = this.Connections[eventId].stop().then((r: any) => {
      console.log("SignalR disconnected: " + r)
    }, (error: any) => {
      console.error(error)
    });
  }

  /** Stops every open hub connection. The entries stay in {@link Connections} so they can be restarted. */
  private stopAllConnections(): void {
    Object.keys(this.Connections).forEach(x => this.stopConnection(x));
  }

  SignalRConnect(event: string): void {
    if (this.Connections[event] == undefined) {
      this.Connections[event] = new signalR.HubConnectionBuilder()
        .configureLogging(signalR.LogLevel.Information)
        .withUrl(this.baseUrl + "/hubs/shiftmaker?event=" + event, {
          accessTokenFactory: () => this.authenticationService.userValue!.jwt
        })
        .withAutomaticReconnect()
        .build()
      let connection: HubConnection = this.Connections[event];
      connection.onreconnected(x => {
        this.selectedDateChangedWrapper(true)
      })

      this.Connections[event].on("ReceiveChange", (response: EmployeeEventResponseDto) => this.applyReceivedChange(response))

      this.Connections[event].on("ReceiveChanges", (responses: EmployeeEventResponseDto[]) => this.applyReceivedChanges(responses))
    }

    this.startConnection(event);
  }

  /**
   * Starts the connection for `event`, waiting out a `stop()` that has not settled yet.
   *
   * The day switch stops every connection and reconnects the new day's events in the SAME synchronous
   * pass, so a revisited event is found in `Disconnecting` — not `Disconnected`. The old guard treated
   * anything other than `Disconnected` as "already live" and did nothing, which left that connection down
   * for good: `withAutomaticReconnect()` reacts to a LOST connection, never to a deliberate stop. The
   * board then showed a stale roster with no error of any kind until a full page reload.
   */
  private startConnection(event: string): void {
    const connection: HubConnection = this.Connections[event];

    // Mid-stop: `start()` would throw. Chain onto the pending stop instead. `stopConnection` never rejects
    // (it handles both settlements), so `then` alone is enough and cannot strand the reconnect.
    if (connection.state == HubConnectionState.Disconnecting) {
      const pending = this.connectionStops[event];
      if (pending) pending.then(() => this.startConnection(event));
      return;
    }

    if (connection.state != HubConnectionState.Disconnected) return;

    connection.start().then(() => {
      console.log("SignalR connected: " + event);
    }).catch((err: any) => {
      return console.error(err);
    });
  }

  /**
   * Applies one `ReceiveChange` broadcast from `ShiftMakerHub` to the local model.
   *
   * Extracted from the SignalR registration so it can be driven directly by the component tests, and so a
   * single unexpected payload can no longer abort the whole handler: every lookup that used to `throw`
   * now degrades to a handled path (Bug #386). A throw here is swallowed by SignalR — it only surfaces as
   * `A callback for the method 'receivechange' threw error 'Error'` in the console — while silently leaving
   * the receiving window half-updated, which is the worst possible failure mode for a live-sync view.
   *
   * @param response the broadcast payload; dates arrive as strings and are normalised here.
   */
  applyReceivedChange(response: EmployeeEventResponseDto): void {
    this.applyReceivedChangeCore(response);
    this.flushUnknownContainerResync();
  }

  /**
   * Applies one broadcast **without** acting on an unknown container.
   *
   * Split out from {@link applyReceivedChange} so a batched `ReceiveChanges` can apply every item first and
   * then decide about the self-heal exactly once, instead of once per item.
   *
   * @param response the broadcast payload.
   */
  private applyReceivedChangeCore(response: EmployeeEventResponseDto): void {
    if (response.start)
      response.start = new Date((response.start as any as string).endsWith("Z") ? (response.start as any as string) : (response.start as any as string) + "Z");
    if (response.end)
      response.end = new Date((response.end as any as string).endsWith("Z") ? (response.end as any as string) : (response.end as any as string) + "Z");
    if (response.employee == undefined) {
      response.employee = new EmployeeNl();
      // @ts-ignore
      response.employee.id = null;
    }

    this.dateHelperService.convertDates(response)

    // check if in responseList
    let old = this.responseList[response.id] as EmployeeEventResponseDto;

    if (old == undefined) {
      // Resolve the container BEFORE touching any shared state: a card whose container this window has not
      // loaded has nowhere to go, and half-registering it (in `responseList`, on the employee) would leave
      // an orphan the board can never render or clean up.
      const target = this.resolveContainer(response.locationId);
      if (target == undefined) return;

      this.responseList[response.id] = response;

      // if an employee is assigned, collapse it onto the one shared instance for that employee
      if (response.employee.id != null) {
        response.employee = this.resolveEmployee(response.employee.id, response.employee);
        response.employee.responses.push(response);
      }

      old = response;

      if (old.manuallyCreated) {
        old.shardName = "Manuell hinzugefügt"
        old.shardId = "manually_added"
      }

      old.responses = target;
      target.push(old);
      moveItemInArray(
        target,
        target.indexOf(old),
        old.index
      )

      this.applyEventTimeFrame(old, response.locationId);
    }

    old.expanded = false;

    // if location was changed
    if (old.locationId != response.locationId || old.index != response.index) {
      const origin = this.resolveContainer(old.locationId);
      const target = this.resolveContainer(response.locationId);

      // Either side unknown ⇒ the move cannot be represented as a transfer. Do the half that CAN be
      // represented rather than nothing, so the board never shows a card somewhere the server says it is
      // not, and never strands one that comes back into view. Everything after this block — employee,
      // active flag, times, stats — still belongs to the card and is still applied; the resync that
      // {@link resolveContainer} just requested rebuilds the board properly.
      if (origin == undefined || target == undefined) {
        // It left a container we can see: take it out.
        if (origin != undefined) origin.splice(origin.indexOf(old), 1);
        // It arrived in a container we can see: put it in.
        if (target != undefined) {
          target.push(old);
          moveItemInArray(target, target.indexOf(old), response.index);
        }

        old.responses = target;
        old.locationId = response.locationId;
        old.index = response.index;

        if (target != undefined) this.applyEventTimeFrame(old, response.locationId);
      } else {
        if (old.locationId == response.locationId) {
          moveItemInArray(
            origin,
            origin.indexOf(old),
            response.index
          )
        } else {
          transferArrayItem(
            origin,
            target,
            origin.indexOf(old),
            response.index
          )

          old.responses = target;
          old.locationId = response.locationId;

          this.applyEventTimeFrame(old, response.locationId);
        }

        old.index = response.index;

        // remove custom created drops when in drop_base
        if (old.locationId == "drop_base" && old.manuallyCreated) {
          target.splice(target.indexOf(old), 1);
          old.employee.responses.splice(old.employee.responses.indexOf(old), 1);
        }
      }
    }

    // Stats recomputed for OTHER employees ride EVERY broadcast, not only the ones that swap the employee
    // (a time edit changes the hours worked just as much). Applying them only inside the
    // `old.employee.id != response.employee.id` guard silently dropped them (Bug #386).
    this.applyUpdatedEmployeeStats(response.updatedEmployeeStats, old.employee);

    // if employee was changed
    if (old.employee.id != response.employee.id) {
      old.employee.responses.splice(old.employee.responses.indexOf(old), 1);

      if (response.employee.id != null) {
        old.employee = this.resolveEmployee(response.employee.id, response.employee);
        old.employee.responses.push(old);
      } else {
        old.employee = response.employee;
      }
    }

    // if set inactive
    if (!response.active) {
      old.employee.responses.splice(old.employee.responses.indexOf(old), 1);
      old.responses?.splice(old.responses?.indexOf(old), 1);
      delete this.responseList[old.id];
    }


    // adapt start and end time
    old.start = response.start;
    old.end = response.end;

    // The stats block is optional on the wire (a card with no employee carries none), so it is guarded —
    // dereferencing `employeeStats.firstShift` blind used to abort the tail of the handler (Bug #386).
    if (old.employee.id != null && response.employee.employeeStats != undefined) {
      old.employee.employeeStats = response.employee.employeeStats;
      old.employee.employeeStats.firstShift = ShiftMakerComponent.toDate(response.employee.employeeStats.firstShift);
    }
  }

  /**
   * Applies one `ReceiveChanges` broadcast — the plural message the hub's batched re-index
   * (`Push*IndexUpdates`) fans out instead of N `ReceiveChange` messages.
   *
   * Without this handler another window would not see a re-ordering at all, because the batch replaces the
   * N single pushes that used to carry it. Items arrive in the order they were applied server-side, and one
   * bad item must not cost the rest of the batch — {@link applyReceivedChange} no longer throws, and the
   * loop is defensive about the argument itself.
   *
   * @param responses the successfully applied items, in server-apply order.
   * @see docs/investigations/386-hub-contract.md §7.2
   */
  applyReceivedChanges(responses: EmployeeEventResponseDto[]): void {
    if (!responses) return;
    // Apply everything first, then decide about the self-heal ONCE — a batch that names the same unknown
    // container ten times must cost one reload, not ten.
    responses.forEach(response => this.applyReceivedChangeCore(response));
    this.flushUnknownContainerResync();
  }

  /**
   * Resolves the card array a broadcast's `locationId` refers to, flagging a self-heal when this window does
   * not know the container.
   *
   * @param locationId `drop_base` (the availability pool) or `drop_{eventId}_{locationId}_{shardId}`.
   * @returns the container's array, or `undefined` when it is not loaded here.
   */
  private resolveContainer(locationId: string): EmployeeEventResponseDto[] | undefined {
    if (locationId == "drop_base") return this.responses;

    const parts = locationId.split("_");
    const container = this.getShiftLocationResponseList(parts[1], parts[2]);
    if (container == undefined) {
      console.warn(`[shift-maker] ReceiveChange: drop container '${locationId}' is not loaded in this window`);
      this.unknownContainerSeen = true;
    }
    return container;
  }

  /**
   * Acts on any unknown container seen while applying the payload that just finished: warns the user and,
   * within the rate limits declared beside {@link resyncMinIntervalMs}, reloads the day so this window picks
   * up the location set it is missing.
   *
   * Always warns — a window whose board disagrees with the server must say so — but only sometimes reloads.
   * Read the block comment on the limits before changing any of this.
   */
  private flushUnknownContainerResync(): void {
    if (!this.unknownContainerSeen) return;
    this.unknownContainerSeen = false;

    const now = Date.now();

    // A long quiet spell means the previous streak ended; this is a new incident, not a continuing loop.
    if (this.lastResyncAt > 0 && now - this.lastResyncAt > ShiftMakerComponent.resyncStreakResetMs) {
      this.consecutiveResyncs = 0;
    }

    const capped = this.consecutiveResyncs >= ShiftMakerComponent.maxConsecutiveResyncs;
    const tooSoon = this.lastResyncAt > 0 && now - this.lastResyncAt < ShiftMakerComponent.resyncMinIntervalMs;

    if (capped || tooSoon) {
      this.globalAlertService.createAndShow("Warnung",
        "Der Dienstplan wurde an anderer Stelle geändert, bitte Seite neu laden.", AlertLevel.warning, 4000);
      return;
    }

    this.lastResyncAt = now;
    this.consecutiveResyncs++;

    this.globalAlertService.createAndShow("Warnung",
      "Der Dienstplan wurde an anderer Stelle geändert, die Ansicht wird aktualisiert.", AlertLevel.warning, 3000);

    // `fromReconnect: true` keeps the live hub connections and the loaded employee list — only the board is
    // rebuilt. `selectedDateChanged` additionally refuses to start while a load is already in flight.
    this.selectedDateChangedWrapper(true);
  }

  /**
   * Resolves the single shared {@link EmployeeNl} instance for an id, registering it in {@link employees} on
   * first use. The shift maker relies on that object identity for its per-employee `responses` bookkeeping
   * (double-booking and overlap warnings), so every response for one employee must point at one instance.
   *
   * @param employeeId the employee to resolve.
   * @param fallback the employee carried by the broadcast, used when the id is unknown to this client — e.g.
   *   someone hired in another window since this one loaded the day. That case previously threw a bare
   *   `Error`, abandoning the rest of the update (Bug #386); it now degrades to the server-supplied data
   *   plus a visible warning telling the user to reload.
   */
  private resolveEmployee(employeeId: string, fallback?: EmployeeNl): EmployeeNl {
    let employee: EmployeeNl | undefined = this.employees[employeeId];
    if (employee != undefined) return employee;

    employee = this.employeeList.find(x => x.id == employeeId);
    if (employee == undefined) {
      employee = fallback ?? new EmployeeNl();
      employee.id = employeeId;
      this.globalAlertService.createAndShow("Warnung",
        "Ein noch unbekannter Mitarbeiter wurde eingeplant, bitte Seite neu laden.", AlertLevel.warning, 3000);
    }

    employee.responses = [];
    this.employees[employeeId] = employee;
    return employee;
  }

  /**
   * Applies the per-employee stats the server recomputed alongside a change (hours worked, salary additions,
   * first shift) onto the shared employee instances.
   *
   * @param updatedEmployeeStats map of employee id → recomputed stats, as broadcast.
   * @param current the employee the changed response currently points at; patched as well, because it is not
   *   guaranteed to be registered in {@link employees} yet.
   */
  private applyUpdatedEmployeeStats(updatedEmployeeStats: any, current?: EmployeeNl): void {
    if (!updatedEmployeeStats) return;

    Object.keys(updatedEmployeeStats).forEach(employeeId => {
      const stats: EmployeeStatsDto = updatedEmployeeStats[employeeId];
      if (!stats) return;
      // `DateHelperService.convertDates` does not touch strings that already carry a `Z`, so parse explicitly.
      stats.firstShift = ShiftMakerComponent.toDate(stats.firstShift);

      const employee: EmployeeNl | undefined = this.employees[employeeId];
      if (employee != undefined) employee.employeeStats = stats;
      if (current != undefined && current.id == employeeId) current.employeeStats = stats;
    })
  }

  /**
   * Stamps the owning event's start/end onto a response so its card can render the event window.
   *
   * @param target the response to stamp.
   * @param locationId a drop-container id of the form `drop_{eventId}_{locationId}_{shardId}`; the
   *   availability pool (`drop_base`) belongs to no event and is skipped.
   */
  private applyEventTimeFrame(target: EmployeeEventResponseDto, locationId: string): void {
    if (locationId == "drop_base") return;

    // Bug #386: this read `split("_")[0]` — the literal string "drop" — so the lookup never matched and the
    // bare `throw` that followed fired on EVERY cross-container move, aborting the handler before index,
    // employee, active and start/end were applied.
    const qEvent = this.events.find(x => x.id == locationId.split("_")[1]);
    if (qEvent == undefined) {
      console.warn(`[shift-maker] ReceiveChange: no loaded event for drop container '${locationId}'`);
      this.globalAlertService.createAndShow("Warnung",
        "Eine Änderung betrifft eine nicht geladene Veranstaltung, bitte Seite neu laden.", AlertLevel.warning, 3000);
      return;
    }

    target.e_start = qEvent.start;
    target.e_end = qEvent.end;
  }

  /**
   * Parses a value that may arrive as an ISO string, a {@link Date}, `null` or `undefined` into a `Date`.
   *
   * @param value the raw value.
   * @returns the parsed date, or `undefined` when nothing usable was supplied.
   */
  private static toDate(value: Date | string | null | undefined): Date | undefined {
    if (!value) return undefined;
    return value instanceof Date ? value : new Date(value);
  }

  reconnect(eventId: string) {
    let connection: HubConnection | undefined = this.Connections[eventId];
    if (connection == undefined) {
      this.globalAlertService.createAlertBannerModel("False", "Ein Fehler ist aufgetreten, bitte Seite neuladen.");
      this.globalAlertService.show();
    } else if (connection.state != HubConnectionState.Disconnected) {

    } else {
      connection.start().then(x => {
        this.selectedDateChangedWrapper()
      }, error => {
        this.globalAlertService.createAlertBannerModel("Fehler", "Verbindung konnte nicht wiederhergestellt werden.", AlertLevel.error, 2000);
        this.globalAlertService.show()
      })
    }
  }

  restoreChanges(previous: EmployeeEventResponseDto, next: EmployeeEventResponseDtoMinimal) {
    // if employee changed
    if (previous.employee.id != next.employeeId) {
      if (previous.employee.id != null) {
        previous.employee.responses.splice(previous.employee.responses.indexOf(previous), 1);
      }

      if (next.employeeId != null) {
        // Bug #386: an unknown employee used to `return` here, abandoning the location/index rollback below
        // and leaving the board showing a move the server had just rejected.
        previous.employee = this.resolveEmployee(next.employeeId);
        previous.employee.responses.push(previous);
      }
    }

    // if location changed
    if (previous.locationId != next.locationId) {
      // Both containers are ones this window is rendering (the user just dragged between them), so a miss
      // here means the board changed underneath the rollback — the same self-heal applies.
      const from = this.resolveContainer(previous.locationId);
      const to = this.resolveContainer(next.locationId);
      if (from != undefined && to != undefined) {
        transferArrayItem(from, to, previous.index, next.index)
        previous.locationId = next.locationId;
        previous.index = next.index;
      }
    }
    // if index changed
    else if (previous.index != next.index && previous.locationId != "drop_base") {
      const container = this.resolveContainer(previous.locationId);
      if (container != undefined) {
        moveItemInArray(container, previous.index, next.index)
        previous.index = next.index;
      }
    }

    // set basic properties
    previous.start = next.start;
    previous.end = next.end;
    previous.e_start = next.e_start;
    previous.e_end = next.e_end;
    previous.active = next.active;

    this.flushUnknownContainerResync();
  }

  async SendChange(response: EmployeeEventResponseDto, previous: EmployeeEventResponseDtoMinimal): Promise<ShiftMakerResponse> {
    try {
      const result: ShiftMakerResponse = await this.Connections[response.eventId]
        .invoke(this.endpoint, new EmployeeEventResponseDtoMinimal(response), previous.employeeId);

      this.adoptNextId(response, result);

      // Since #386/#392 the hub answers a refusal with a typed `ApiError` instead of throwing, so a rejected
      // push no longer reaches the `catch` below — without this the board would keep showing an optimistic
      // change the server never persisted (that is exactly the shape of the #392 employee defect).
      if (result != undefined && !result.success && result.error != undefined) {
        this.restoreChanges(response, previous);
        // German mapping only — `result.error.description` is the server's English `ErrorCatalog` text and
        // must never reach this banner directly (Bug: shift plan showed English error text, see #386/#392).
        this.globalAlertService.createAndShow(
          "Fehler", ShiftMakerComponent.messageFor(result.error.code), AlertLevel.error, 3000);
      }

      return result;

    } catch (e) {
      console.error(e)
      this.restoreChanges(response, previous);
      return new ShiftMakerResponse();
    }
  }

  /**
   * Pushes the new `index` of every card in one container as a **single** hub invocation.
   *
   * The board used to fire one `SendChange` per sibling after a drop and per card on `sort()`. SignalR's
   * `MaximumParallelInvocationsPerClient = 1` serialises those, so a 20-card container queued 21 round trips
   * behind the user's next edit — the leading explanation for "only my first change shows up live" (Bug
   * #386). The hub's batch entry point applies the items strictly in list order through the same code path as
   * the single push, and fans one `ReceiveChanges` message out to the other windows.
   *
   * @param responses the container array, already in its final client-side order.
   * @param eventId the event whose hub connection carries the push.
   * @returns the per-item outcomes, or an all-empty envelope when the connection rejected the invocation.
   * @see docs/investigations/386-hub-contract.md
   */
  async SendIndexUpdates(responses: EmployeeEventResponseDto[], eventId: string): Promise<ShiftMakerBatchResponse> {
    // A card that has never been persisted (`createShift()` leaves `id === ""`) has no row to restamp.
    const batch = responses.filter(x => x.id);
    if (batch.length == 0) return new ShiftMakerBatchResponse();

    // The availability pool spans every event of the day, so the container's own event is not necessarily
    // connected; the hub broadcasts each item to its own event group regardless of which connection carried
    // the batch, so any live connection will do.
    const connection = this.Connections[eventId]
      ?? this.Connections[batch.find(x => this.Connections[x.eventId] != undefined)?.eventId ?? ""];
    if (connection == undefined) return new ShiftMakerBatchResponse();

    try {
      const result: ShiftMakerBatchResponse = await connection
        .invoke(this.batchEndpoint, batch.map(x => new EmployeeEventResponseDtoMinimal(x)));

      if (result == undefined) return new ShiftMakerBatchResponse();

      // Per-item outcomes come back at the same positions the items were submitted at.
      result.results?.forEach((itemResult, index) => {
        const response = batch[index];
        if (response == undefined) return;
        this.adoptNextId(response, itemResult);
        if (itemResult.success) this.applyPushResult(response, itemResult);
      });

      if (!result.success && result.error != undefined) {
        // German mapping only — see the single-push `SendChange` above for why `.description` is not used.
        this.globalAlertService.createAndShow(
          "Fehler", ShiftMakerComponent.messageFor(result.error.code), AlertLevel.error, 3000);
      }

      return result;

    } catch (e) {
      console.error(e)
      this.globalAlertService.createAndShow("Fehler",
        "Die Reihenfolge konnte nicht gespeichert werden, bitte Seite neu laden.", AlertLevel.error, 3000);
      return new ShiftMakerBatchResponse();
    }
  }

  /**
   * Copies the employee stats the hub returned for a successful push back onto the local employee.
   *
   * Every `SendChange` call site repeated this block verbatim; it is one method so the batch path behaves
   * identically to the single-push path.
   *
   * @param response the card that was pushed.
   * @param result the hub's reply for that card.
   */
  private applyPushResult(response: EmployeeEventResponseDto, result: ShiftMakerResponse): void {
    if (!result.success || result.employeeEventResponse == undefined) return;

    this.dateHelperService.convertDates(result);
    if (response.employee.id == null) return;

    response.employee.employeeStats = result.employeeEventResponse.employee.employeeStats;
    response.employee.employeeStats.firstShift =
      ShiftMakerComponent.toDate(result.employeeEventResponse.employee.employeeStats.firstShift);
  }

  /**
   * Adopts the server-assigned id (`ShiftMakerResponse.nextId`) onto the local response and re-keys
   * {@link responseList} so later broadcasts for that row resolve to this object.
   *
   * `nextId` has been on the wire since 2023 and was never read (Bug #386): a card created locally by
   * {@link createShift} carries `id === ""`, so until the server's own broadcast happened to arrive the local
   * object had no identity at all.
   *
   * @param response the local response that was just pushed.
   * @param result the hub's reply.
   */
  private adoptNextId(response: EmployeeEventResponseDto, result: ShiftMakerResponse | undefined): void {
    if (!result || !result.success) return;

    const nextId = result.nextId;
    if (!nextId || nextId == response.id) return;

    // The hub broadcasts to the caller as well, so a *separate* object may already be registered under this
    // id by `applyReceivedChange`. That one is authoritative and the local placeholder is about to be
    // discarded by the caller — re-keying onto it here would duplicate the card.
    const existing = this.responseList[nextId] as EmployeeEventResponseDto | undefined;
    if (existing != undefined && existing !== response) return;

    if (response.id && this.responseList[response.id] === response) delete this.responseList[response.id];

    response.id = nextId;
    this.responseList[nextId] = response;
  }

  onResize(event: any) {
    let width: number = event.target.innerWidth;
    if (width >= 1536 && !this.large) {
      this.large = true;
    } else if (width < 1536 && this.large) {
      this.large = false;
    }
  }

  onScroll(event: any) {
    if (event.target.innerWidth < 768) return;

    const {x, y} = this.employeeHeader.nativeElement.getBoundingClientRect();
    this.height = event.target.lastChild.clientHeight - y;
  }

  assignEvents(large: boolean) {
    if (large) {
      this.filteredEvents = Object.assign([], this.events);
    } else {
      this.filteredEvents = this.showingEvent ? [this.showingEvent] : [];
    }
  }

  /**
   * Loads the selected day from the shift plan's own endpoint and rebuilds the board (#409).
   *
   * This used to be a hand-rolled continuation chain over four requests — company locations, then the employee
   * roster, then a nested `getDisabledLocations` → `getEmployeeAvailability` pair — spread across four
   * controllers with four different inherited authorization attributes. That smear is precisely how #280 could
   * add `ShiftLead` to the shift plan's *writes* and leave every *read* answering 403. It is now one call
   * against one controller with one gate, taken by every caller regardless of role, so the two halves cannot
   * drift apart again.
   *
   * @param fromReconnect `true` on the SignalR-reconnect / self-heal path, which keeps the live hub connections
   *   up instead of tearing them down and rebuilding them.
   */
  selectedDateChangedWrapper(fromReconnect: boolean = false) {
    // Unchanged guard: a second load started while one is in flight would interleave two board rebuilds.
    if (this.isUpdating) return;
    this.isUpdating = true;

    this.setDownloadTimespan(pickerDateToDayKey(this.selectedDay.date))
    this.edit = !this.platformScannerService.isMobile()

    // First resolve the company zone (cached after the first load): the selected day travels as a plain COMPANY
    // day and every shift time on the board is read in that zone (#645/#651).
    this.companyTimeZone.load().pipe(
      switchMap(() => this.shiftPlanHttpService.getDay(this.selectedDayKey()))
    ).subscribe({
      next: day => this.applyDay(day, fromReconnect),
      error: err => {
        console.error(err);
        // A refusal from this endpoint is a structured `ApiError`, and its CODE is the only thing that
        // separates "there is no event running right now" from "the server is broken" — never its
        // `description`, which is the server's English `ErrorCatalog` text (developer-facing by design).
        // Every one of the six calls this replaced answered a bare 403 and the screen showed generic copy —
        // which is what made the shift lead's empty board unexplained rather than merely empty.
        this.globalAlertService.createAlertBannerModel("Fehler",
          ShiftMakerComponent.messageFor(err?.error?.code, "Beim Laden ist ein Fehler aufgetreten, bitte Seite aktualisieren."),
          AlertLevel.error, 4000);
        this.globalAlertService.show()

        this.isUpdating = false;
      }
    })
  }

  /**
   * Rebuilds the whole board from one {@link ShiftPlanDay} payload.
   *
   * Split out of {@link selectedDateChangedWrapper} so the assembly can be driven directly by the component
   * tests — the same reason {@link applyReceivedChange} is separate from its SignalR registration.
   *
   * @param day the aggregate for the selected day, already narrowed to what this session may see.
   * @param fromReconnect see {@link selectedDateChangedWrapper}.
   */
  /**
   * „Kassen-Event Sommerfest, virtuelles Event Teambesprechung" — or `undefined` when nothing is hidden.
   *
   * ONE getter and no stored field, deliberately: it is a pure projection of {@link hiddenEvents}, which is
   * already reset and refilled by {@link applyDay}, so a second copy could only ever go stale. `undefined`
   * rather than an empty string is what lets the template's `@if (…; as label)` decide whether the banner
   * exists at all — an empty banner is worse than none.
   *
   * Each name is followed by its kind, because the two are the reason the flag exists: a venue books an
   * evening's takings through one Kassen-Event and plans the shifts across virtual ones, so „which of them is
   * missing" is exactly the question the reader has.
   */
  get hiddenEventsLabel(): string | undefined {
    if (this.hiddenEvents.length === 0) return undefined;
    return this.hiddenEvents
      .map(event => `${event.name} (${event.kind === EventKind.Virtual ? 'virtuell' : 'Kassen-Event'})`)
      .join(', ');
  }

  applyDay(day: ShiftPlanDay, fromReconnect: boolean = false) {
    this.scope = day.scope ?? new ShiftPlanScope();
    this.settings = day.settings ?? new ShiftMakerSettings();

    // Replaced wholesale, never mutated in place: `employeeOptions`' cache keys on this array's IDENTITY, so an
    // in-place update would leave every dropdown showing the previous day's eligible set (#403).
    this.employeeList = day.employees ?? [];
    this.employeeList.forEach(x => {
      x.name = x.firstName + " " + x.lastName
    })

    // The company's active locations now arrive fresh with every day, which is why the old
    // `latestDisabledLocations` bookkeeping is gone: it existed solely to un-merge YESTERDAY's response-bearing
    // locations back out of a `companyLocations` that was fetched once and then mutated for the life of the
    // component. There is nothing to un-merge from a dictionary that was just replaced.
    this.companyLocations = day.companyLocations ?? {};
    Object.entries(day.disabledLocations ?? {}).forEach(([shard, locations]) => {
      if (!this.companyLocations[shard]) this.companyLocations[shard] = [];
      (locations ?? []).forEach((v: LocationDto) => {
        if (this.companyLocations[shard].findIndex((b: LocationDto) => b.id == v.id) > -1) return;
        this.companyLocations[shard].push(v)
      })
    })

    // reset everything
    this.employees = {};
    this.events = [];
    this.hiddenEvents = [];
    this.filteredEvents = [];
    this.responses = [];
    this.shifts = [];
    this.responseList = {};

    if (!this.selectedDay) {
      // Used to be a bare `return`, which latched `isUpdating` at true and wedged every later load.
      this.isUpdating = false;
      return;
    }

    // signalr disconnect
    if (!fromReconnect) {
      this.stopAllConnections();
    }

    this.events = day.events ?? [];
    this.hiddenEvents = day.hiddenEvents ?? [];
    if (this.events.length > 0) this.showingEvent = this.events[0];
    else this.showingEvent = undefined;
    this.assignEvents(this.large)

    // create rooms
    this.shifts = [];
    this.events.forEach(x => {
      let storage = new ShiftStorage(x);
      // `?? []` rather than a bare index: an event whose shard contributed no locations to this payload used to
      // throw here and abort the entire board build. The cards for such an event are then reported by the
      // skip counter below, which is a far better outcome than a blank screen.
      (this.companyLocations[x.shardId] ?? []).forEach((l: LocationNl) => {
        storage.shiftLocations.push(new ShiftLocation(l))
      })
      this.shifts.push(storage);
    })

    if (!fromReconnect) {
      // signalr connect
      this.events.forEach(e => {
        this.SignalRConnect(e.id)
      })
    }

    /*
     * Cards the day's own payload cannot be placed from — its event is not among the events this load
     * returned, or its location is not among the containers this load created.
     *
     * ONE counter for BOTH conditions, deliberately. They are the same fact to the user ("some
     * availabilities of this day cannot be shown"), they almost always have the same cause (a card
     * pointing at an event/location the day-load did not return — an unknown event makes the container
     * unknown too), and `GlobalAlertService` keeps a single banner, so two warnings would mean the
     * second silently replaces the first. One counter, one banner, copy that names neither half.
     *
     * Both used to `throw` — the event check aborted the WHOLE load on the first bad card, leaving an
     * empty or half-built board with no explanation (Bug #386).
     *
     * Deliberately NOT routed through `resolveContainer`: this IS the day load, so requesting the
     * receive path's self-heal reload from inside it would loop. A card the load itself cannot place is
     * a server/tenant-configuration disagreement, not staleness — reloading cannot fix it.
     *
     * Since #409 the server can legitimately narrow the payload for a `ShiftLead`, but it narrows events and
     * responses TOGETHER, so a narrowed day still places every card it ships. A non-zero count here therefore
     * still means what it always meant.
     */
    let skipped = 0;
    (day.responses ?? []).forEach(r => {
      const inPool = r.locationId == "drop_base";
      const split = r.locationId.split("_");
      // The pool spans the whole day and belongs to no single event, so it carries no event window.
      const qEvent = inPool ? undefined : this.events.find(x => x.id == split[1]);
      const container = inPool ? this.responses : this.getShiftLocationResponseList(split[1], split[2]);

      // Validate BEFORE mutating anything. Registering the card first and bailing out later is how the
      // old code left orphans behind — a row in `responseList` and an entry on the employee that no
      // container ever renders and no later broadcast can clean up.
      if ((!inPool && qEvent == undefined) || container == undefined) {
        console.warn(`[shift-maker] day load: cannot place response ${r.id} at '${r.locationId}'`
          + ` (event ${qEvent == undefined ? "unknown" : "known"}, container ${container == undefined ? "unknown" : "known"})`);
        skipped++;
        return;
      }

      r.expanded = false;
      this.responseList[r.id] = r;

      if (qEvent != undefined) {
        r.e_start = qEvent.start;
        r.e_end = qEvent.end;
      }

      if (r.manuallyCreated && !r.employee) {
        r.employee = new EmployeeNl();
        // @ts-ignore
        r.employee.id = null;
      }

      if (r.manuallyCreated) {
        r.shardName = "Manuell hinzugefügt"
        r.shardId = "manually_added"
      }

      // putting the responses in the right location
      container.push(r);
      r.responses = container;

      if (r.employee.id != null) {
        // creating one-object employees
        if (!this.employees[r.employee.id]) {
          this.employees[r.employee.id] = r.employee;
          r.employee.responses = [];
        } else r.employee = this.employees[r.employee.id] as EmployeeNl;
      }

      r.employee.responses.push(r);
    })

    if (skipped > 0) {
      this.globalAlertService.createAndShow("Warnung",
        "Einige Verfügbarkeiten konnten nicht zugeordnet werden und werden nicht angezeigt.",
        AlertLevel.warning, 4000);
    }

    // One batched request for everybody on the board (#558), after `this.employees` is complete — asking
    // earlier would ask for whoever happened to be indexed so far.
    this.loadContactQuickLinks();

    if (this.nextSelectedEvent) {
      let event = this.events.find(x => x.id == this.nextSelectedEvent);
      if (event != undefined) {
        this.showingEvent = event;
        this.assignEvents(this.large);
      }
    }

    this.isUpdating = false;
  }

  /**
   * Loads the month calendar's event markers (#409).
   *
   * A separate call from {@link selectedDateChangedWrapper} on purpose: its range is the month the calendar is
   * *showing*, not the day that is *selected*, so paging the calendar must not re-read the board and picking a
   * day within the shown month must not re-read the month.
   */
  dateRangeChanged() {
    // The picker's first and last visible cells as plain company days, END INCLUSIVE (no +1 on the client).
    this.companyTimeZone.load().pipe(
      switchMap(timeZone => this.shiftPlanHttpService.getCalendar(
        pickerDateToDayKey(this.calendarRange!.item1!),
        pickerDateToDayKey(this.calendarRange!.item2!)
      ).pipe(map(calendar => ({timeZone, calendar}))))
    ).subscribe({
      next: ({timeZone, calendar}) => {
        // Each dot sits on the COMPANY day of its event start (#656).
        this.calendarEntries = (calendar.events ?? [])
          .map(x => new CalendarEntry(x.start, x.end, x.color, x.id, timeZone))
      },
      error: err => {
        console.error(err);
        // A shift lead's calendar is narrowed to the single day they can act on rather than refused, so a real
        // error here is a real error — but the typed CODE still beats the generic copy when it is a "no
        // event is running" refusal reaching this call first. Mapped to German, never `.description` — see
        // `messageFor`'s doc.
        this.globalAlertService.createAlertBannerModel("Fehler",
          ShiftMakerComponent.messageFor(err?.error?.code, "Events konnten nicht geladen werden."),
          AlertLevel.error, 2000);
        this.globalAlertService.show();
      }
    })
  }

  getEventName(id: string): string | undefined {
    return this.events.find(x => x.id == id)?.name
  }

  getEmployeeName(id: string): string | undefined {
    let employee = this.employeeList.find(x => x.id == id);
    if (employee == undefined) return undefined;
    return (employee.firstName + " " + employee.lastName).trim()
  }

  createShift() {
    let shift = this.responses.find(x => x.shardId == "manually_added")
    if (shift != undefined) {
      this.globalAlertService.createAlertBannerModel("Warnung", "Es darf sich nur ein freier Mitarbeiterplatz in der Auswahl befinden.", AlertLevel.warning, 2000);
      this.globalAlertService.show()
      return;
    }


    shift = new EmployeeEventResponseDto();
    shift.index = 0;
    shift.responses = this.responses;
    shift.locationId = "drop_base";
    shift.shardName = "Manuell hinzugefügt"
    shift.shardId = "manually_added"
    shift.active = true;
    shift.employee = new EmployeeNl();
    // @ts-ignore
    shift.employee.id = null;
    shift.manuallyCreated = true;
    this.responses.splice(0, 0, shift);
  }

  getEmployees(): EmployeeNl[] {
    return Object.values(this.employees);
  }

  setEmployee(response: EmployeeEventResponseDto, employeeId: string) {
    let previous: EmployeeEventResponseDtoMinimal = new EmployeeEventResponseDtoMinimal(response);
    let pEmployee = response.employee
    if (employeeId == null) {
      response.employee.responses.splice(response.employee.responses.indexOf(response), 1);
      response.employee = new EmployeeNl();
      response.employee.id = employeeId;
    } else {
      let lEmployee: EmployeeNl | undefined = this.employees[employeeId];
      if (!lEmployee) {
        lEmployee = this.employeeList.find(x => x.id == employeeId);
        if (lEmployee) {
          lEmployee.responses = [];
          this.employees[employeeId] = lEmployee;
        }
      }
      if (!lEmployee) {
        this.globalAlertService.createAlertBannerModel("Fehler", "Mitarbeiter konnte nicht gespeichert werden.", AlertLevel.error, 2000);
        this.globalAlertService.show();
        return;
      }

      response.employee.responses.splice(response.employee.responses.indexOf(response), 1);
      response.employee = lEmployee;
      response.employee.responses.push(response);
    }

    if (response.locationId != "drop_base") this.SendChange(response, previous).then(r => {
      if (!r.success) return;
      this.applyPushResult(response, r);
      this.applyUpdatedEmployeeStats(r.employeeEventResponse?.updatedEmployeeStats, response.employee);
    })

    if (this.platformScannerService.getPlatform() == Platform.Safari) {
      let element: HTMLElement = document.getElementById(response.locationId) as HTMLElement;
      element.click();
    }
  }

  disableResponse(response: EmployeeEventResponseDto) {
    let c = confirm(`Bitte das Deaktivieren der Verfügbarkeit für ${response.employee.firstName} ${response.employee.lastName} bestätigen.`);
    if (!c) return;

    let previous: EmployeeEventResponseDtoMinimal = new EmployeeEventResponseDtoMinimal(response);
    if (response.locationId != "drop_base") {
      this.globalAlertService.createAlertBannerModel("Fehler", "Es können nur nicht genutzte Mitarbeiter-Verfügbarkeiten entfernt werden.", AlertLevel.error, 2000);
      this.globalAlertService.show();
      return;
    }
    if (response.manuallyCreated) {
      this.globalAlertService.createAlertBannerModel("Fehler", "Um eine manuell erstellte Verfügbarkeit zu löschen bitte erneut in die verfügbare Liste ziehen.", AlertLevel.error, 2000);
      this.globalAlertService.show();
    }

    response.active = false;

    this.SendChange(response, previous).then(r => {
      if (r.success) {
        this.globalAlertService.createAlertBannerModel("Erfolg", "Verfügbarkeit wurde deaktiviert.", AlertLevel.success, 2000);
        this.globalAlertService.show()
      } else if (r.error == undefined) {
        // A typed refusal has already been surfaced (with its own, specific description) by `SendChange`;
        // re-raising the generic message here would only clobber it.
        this.globalAlertService.createAlertBannerModel("Fehler", "Beim Speichern ist ein Fehler aufgetreten.", AlertLevel.error, 2000);
        this.globalAlertService.show();
      }
    })
  }

  getShardLocations(shard: string): LocationNl[] | undefined {
    if (!this.companyLocations || !this.companyLocations[shard]) return undefined;
    return (this.companyLocations[shard] as LocationNl[])
    //.sort((a,b) => {
    //if (a.order - b.order != 0) return a.order - b.order;
    //if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
    //if (a.name.toLowerCase() > b.name.toLowerCase()) return -1;
    //return 0;
    //});
  }

  getEventColor(id: string): string {
    return this.events.find(x => x.id == id)?.color ?? "#000000";
  }

  /**
   * Looks up the card array of one drop container (an event's location).
   *
   * Returns `undefined` when this window has not loaded that container — a location disabled or created in
   * another window since the day was loaded, or an event outside the current selection. It used to `throw`
   * a bare `Error` instead, which every caller had to remember to guard: inside a SignalR callback that
   * throw was swallowed and left the receiving window half-updated (Bug #386). Callers now decide — the
   * receive path warns and resyncs, the day-load skips the card, the template renders an empty container.
   *
   * @param eventId the event that owns the container.
   * @param locationId the location within that event.
   * @returns the container's live array, or `undefined` when it is not loaded here.
   */
  getShiftLocationResponseList(eventId: string, locationId: string): EmployeeEventResponseDto[] | undefined {
    return this.shifts.find(x => x.event.id == eventId)?.shiftLocations.find(x => x.location.id == locationId)?.responses;
  }

  /**
   * The template-facing form of {@link getShiftLocationResponseList}: never `undefined`, so the bindings
   * that read `.length` or iterate it stay simple.
   *
   * An unknown container yields a **cached** empty array rather than a fresh literal, because the value is
   * read during every change-detection pass and feeds `[cdkDropListData]` — a new array each pass would make
   * the drop list re-register endlessly. The cache is keyed per container so two unknown containers never
   * share (and therefore never merge) their cards.
   *
   * @param eventId the event that owns the container.
   * @param locationId the location within that event.
   * @returns the container's live array, or a stable empty stand-in.
   */
  getShiftLocationResponses(eventId: string, locationId: string): EmployeeEventResponseDto[] {
    const responses = this.getShiftLocationResponseList(eventId, locationId);
    if (responses != undefined) return responses;

    const key = `${eventId}_${locationId}`;
    if (this.unknownContainers[key] == undefined) this.unknownContainers[key] = [];
    return this.unknownContainers[key];
  }

  // ---------------------------------------------------------------------------------------------------------
  // Employee dropdown options (#403)
  // ---------------------------------------------------------------------------------------------------------

  /*
   * Why these are memoised — do NOT turn them back into plain getters.
   *
   * Both are read from inside `@for` loops over the day's cards, on EVERY card of EVERY event, so a naive
   * implementation runs `filter` over the whole roster once per card per change-detection pass. Worse, it
   * would return a NEW array each time: `ng-select`'s `[items]` and `@for`'s tracking both compare by
   * reference, so a fresh array per pass re-initialises the dropdown continuously (this is exactly the trap
   * `getShiftLocationResponses` documents for `[cdkDropListData]`).
   *
   * The inputs only change wholesale: `loadEmployees` REPLACES `employeeList`, and the day load REPLACES
   * `events` (whose `employedIds` ride along). Comparing those two array references is therefore a complete
   * and O(1) staleness check — no deep compare, no change-detection hook. The per-card selection is part of
   * the cache key because the currently assigned employee is always kept in the list (see below), which is
   * bounded by the number of distinct employees on the board.
   */

  /** Cached option lists, keyed `{scope}|{selectedEmployeeId}`; invalidated wholesale, see the block above. */
  private employeeOptionCache: {[key: string]: EmployeeNl[]} = {};
  /** The `events` array the cache was built from; a different reference invalidates it. */
  private employeeOptionEvents: EventNl[] | undefined;
  /** The `employeeList` array the cache was built from; a different reference invalidates it. */
  private employeeOptionRoster: EmployeeNl[] | undefined;

  /**
   * The employees a **manually created card of one event** may be assigned to: everyone employed at that
   * event's start (Bug #403).
   *
   * The roster (`employeeList`) is deliberately wider than this — the server returns everyone employed at any
   * point in the selected day's month so historical names still resolve — which is why the dropdown used to
   * offer people who had already left, or whose employment only starts later in the month.
   *
   * @param event the event whose card is being edited; its `employedIds` is the eligible set.
   * @param response the card, so its currently assigned employee is never dropped from the list.
   * @returns a stable array; the same reference until the roster or the day is reloaded.
   */
  getEventEmployeeOptions(event: EventNl, response: EmployeeEventResponseDto): EmployeeNl[] {
    return this.employeeOptions(event.id, response, () => event.employedIds ?? []);
  }

  /**
   * The employees a **manually created card in the availability pool** may be assigned to.
   *
   * Pool cards sit in `drop_base` and belong to no single event, so the eligible set is the UNION of the
   * eligible sets of the day's events — a person employed for any one of them can legitimately be planned
   * onto it.
   *
   * **No-events fallback.** With no events loaded the union is empty, which would leave the dropdown
   * unusable. It then falls back to "employed on the selected day" — `EmployeeNl.isEmployedOnQueryDate`,
   * which the server computes over the employment stints for exactly the date this roster was requested for.
   * It is NOT derived here from `joined`/`left`: those are an outer envelope and answer the question wrongly
   * across a gap in employment. (Creating a pool card requires at least one event, so this path is reached
   * only by a card that was already there — but an unusable dropdown would strand it.)
   *
   * @param response the card, so its currently assigned employee is never dropped from the list.
   * @returns a stable array; the same reference until the roster or the day is reloaded.
   */
  getPoolEmployeeOptions(response: EmployeeEventResponseDto): EmployeeNl[] {
    return this.employeeOptions('drop_base', response, () => {
      if (this.events.length == 0) return undefined;
      const union = new Set<string>();
      this.events.forEach(e => (e.employedIds ?? []).forEach(id => union.add(id)));
      return union;
    });
  }

  /**
   * Shared body of {@link getEventEmployeeOptions} / {@link getPoolEmployeeOptions}: cache lookup, staleness
   * check and the "keep the current selection" rule.
   *
   * @param scope cache scope — an event id, or `drop_base` for the pool.
   * @param response the card being edited.
   * @param eligible produces the eligible ids, or `undefined` to fall back to "employed on the selected day".
   */
  private employeeOptions(scope: string, response: EmployeeEventResponseDto,
                          eligible: () => Iterable<string> | undefined): EmployeeNl[] {
    if (this.employeeOptionEvents !== this.events || this.employeeOptionRoster !== this.employeeList) {
      this.employeeOptionEvents = this.events;
      this.employeeOptionRoster = this.employeeList;
      this.employeeOptionCache = {};
    }

    // A card with no employee yet reports `null`; keep that out of the key's value space.
    const selectedId: string = response?.employee?.id ?? '';
    const key = `${scope}|${selectedId}`;

    let cached = this.employeeOptionCache[key];
    if (cached != undefined) return cached;

    const ids = eligible();
    const allowed = ids == undefined ? undefined : new Set<string>(ids);

    cached = this.employeeList.filter(x =>
      // Never hide the person the card already names: a stint can be edited after the fact, and blanking the
      // dropdown would silently drop the assignment the next time it is touched. The server-side gate
      // (`ShiftMakerErrorCodes.EmployeeNotEmployed`) still refuses an ineligible NEW assignment.
      x.id == selectedId
      || (allowed == undefined ? x.isEmployedOnQueryDate : allowed.has(x.id)));

    this.employeeOptionCache[key] = cached;
    return cached;
  }

  availabilityDropped(event: CdkDragDrop<EmployeeEventResponseDto[]>) {
    let previous: EmployeeEventResponseDtoMinimal = new EmployeeEventResponseDtoMinimal(event.previousContainer.data[event.previousIndex]);
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);

      // update response dto for new index
      event.container.data[event.currentIndex].index = event.currentIndex;
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      )

      // update response dto for new location
      event.container.data[event.currentIndex].locationId = event.container.id;
      event.container.data[event.currentIndex].index = event.currentIndex;

      event.container.data[event.currentIndex].responses = event.container.data;
      if (event.container.id != "drop_base" && event.container.data[event.currentIndex].shardId != "manually_added" && event.container.id.split("_")[3] != event.container.data[event.currentIndex].shardId) {
        this.globalAlertService.createAlertBannerModel("Warnung", "Mitarbeiter hat Verfügbarkeit für einen anderen Ort angegeben!", AlertLevel.warning, 2000);
        this.globalAlertService.show();
      } else if (event.container.id != "drop_base" && event.container.data[event.currentIndex].shardId != "manually_added" && event.container.id.split("_")[1] != event.container.data[event.currentIndex].eventId) {
        this.globalAlertService.createAlertBannerModel("Warnung", "Mitarbeiter hat Verfügbarkeit für eine andere Veranstaltung angegeben!", AlertLevel.warning, 2000);
        this.globalAlertService.show();
      }
    }


    if (event.container.id == "drop_base" && event.container.data[event.currentIndex]) {
      event.container.data[event.currentIndex].start = undefined;
      event.container.data[event.currentIndex].end = undefined;
    } else {
      let qEvent = this.events.find(x => x.id == event.container.id.split("_")[1]);

      if (qEvent == null) return;

      if (event.container.data[event.currentIndex] && !event.container.data[event.currentIndex].start) {
        event.container.data[event.currentIndex].start = qEvent.start;
        event.container.data[event.currentIndex].e_start = qEvent.start;
      }
      if (event.container.data[event.currentIndex] && !event.container.data[event.currentIndex].end) {
        // event.container.data[event.currentIndex].end = qEvent.end;
        event.container.data[event.currentIndex].e_end = qEvent.end;
      }
    }

    if (event.container.data[event.currentIndex].shardId == "manually_added" && event.container.id != "drop_base") {
      event.container.data[event.currentIndex].eventId = event.container.id.split("_")[1];
    }

    let old = event.container.data[event.currentIndex];
    this.SendChange(event.container.data[event.currentIndex], previous).then(r => {
      if (r.success) {
        if (old.manuallyCreated && old.locationId == "drop_base") {
          event.container.data.splice(event.container.data.indexOf(old), 1);
          old.employee.responses.splice(old.employee.responses.indexOf(old), 1);
        } else if (old.manuallyCreated && old.locationId != "drop_base" && old.id == "") {
          if (old.employee && old.employee.id != null) {
            old.employee.responses.splice(old.employee.responses.indexOf(old), 1);
          }
          event.container.data.splice(event.container.data.indexOf(old), 1);
        }
      }

      if (r.success) {
        this.applyPushResult(old, r);

        if (!old.responses) return;
        // Restamp the whole container in ONE invocation. This used to be one `SendChange` per sibling —
        // `1 + N` strictly serialized round trips per drop, every one of which threw when the container was
        // the availability pool (Bug #386 / the Sentry KeyNotFoundException storm).
        old.responses.forEach(x => x.index = old.responses!.indexOf(x));
        this.SendIndexUpdates(old.responses, old.eventId);
      }
    });


  }

  getDropArray(): string[] {
    let dropIds: string[] = [];
    this.shifts.forEach(storage => {
      storage.shiftLocations.forEach(location => {
        dropIds.push("drop_" + storage.event.id + "_" + location.location.id + "_" + storage.event.shardId)
      })
    })
    dropIds.push("drop_base");
    return dropIds;
  }

  getEventIndex(event: EventNl): number {
    return this.events.indexOf(event);
  }

  getPreviousEvent(event: EventNl): EventNl {
    return this.events[(this.getEventIndex(event) + (this.events.length - 1)) % this.events.length];
  }

  getNextEvent(event: EventNl): EventNl {
    return this.events[(this.getEventIndex(event) + 1) % this.events.length]
  }

  isColliding(response: EmployeeEventResponseDto): boolean {
    return (response.responses?.filter(x => x.start?.toISOString() == response.start?.toISOString() && x != response).length ?? 0) > 0;
  }

  // if time is over 24 hours
  timeWarning(response: EmployeeEventResponseDto): boolean {
    if (response.start == undefined || response.end == undefined) return false;
    return response.end.getTime() - response.start.getTime() >= 86400000;
  }

  firstShift(response: EmployeeEventResponseDto): boolean {
    if (response.employee.id == null) return false;
    if (response.employee.employeeStats.firstShift == undefined) return true;

    if (response.start == undefined) {
      let event = this.events.find(x => x.id == response.eventId);
      if (event == undefined) return false;

      return event.start <= response.employee.employeeStats.firstShift;
    }
    return response.start <= response.employee.employeeStats.firstShift;
  }

  hasRelationship(employee: EmployeeNl): boolean {
    return employee.employeeStats.employmentRelationship != undefined;
  }

  isOverLimit(stats: EmployeeStatsDto): boolean {
    if (!stats.employmentRelationship?.limit || !stats.hoursWorked) return false;
    return stats.employmentRelationship.limit < EmployeeStatsHelper.getTotal(stats)
  }

  alreadyPlanned(response: EmployeeEventResponseDto): boolean {
    return response.employee.responses.find(x => x.locationId != "drop_base") != undefined;
  }

  isDoubleEmployee(response: EmployeeEventResponseDto): boolean {
    if (!response.employee) return false;
    response.employee.responses = response.employee.responses.sort((a, b) => {
      if (!a.start || !b.start) return 0;
      if (a.start < b.start) return -1;
      if (a.start > b.start) return 1;
      return 0;
    })

    if (response.employee.responses.length < 2) return false;
    for (let i = 1; i < response.employee.responses.length; i++) {
      if (!response.employee.responses[i].start || !response.employee.responses[i - 1].end) continue;
      if (response.employee.responses[i].start! < response.employee.responses[i - 1].end!) return true;
    }
    return false;
  }

  isOverlapping(response: EmployeeEventResponseDto): boolean {
    if (!response.employee) return false;

    let overlapping = false;
    let comparable = response.employee.responses.filter(x => x.locationId != "drop_base");
    comparable = comparable.sort((a, b) => {
      if ((a.start ?? a.e_start!) < (b.start ?? b.e_start!)) return -1;
      if ((a.start ?? a.e_start!) > (b.start ?? b.e_start!)) return 1;
      if ((a.end ?? a.e_end!) < (b.end ?? b.e_end!)) return -1;
      if ((a.end ?? a.e_end!) > (b.end ?? b.e_end!)) return 1;
      if (a.locationId != "drop_base" && b.locationId != "drop_base") overlapping = true;
      return 0;
    })
    if (overlapping) return true;

    if (comparable.length < 2) return false;
    for (let i = 1; i < comparable.length; i++) {
      if ((comparable[i].start ?? comparable[i].e_start!) < (comparable[i - 1].end ?? comparable[i - 1].e_end!)) return true;
    }
    return false;
  }

  isEndBeforeStart(response: EmployeeEventResponseDto): boolean {
    if (!response.start || !response.end) return false;
    return response.start >= response.end;
  }

  isTimeNotSet(response: EmployeeEventResponseDto, key: Side): boolean {
    if (key == Side.Left) return response.start == undefined;
    if (key == Side.Right) return response.end == undefined;
    return false;
  }

  /**
   * Sorts one container by start time and persists the new order.
   *
   * One invocation for the whole container, not one per card (Bug #386): clicking "sort" on a 20-card
   * location used to enqueue 20 serialized hub round trips.
   *
   * @param responses the container array; sorted in place.
   */
  sort(responses: EmployeeEventResponseDto[]) {
    responses.sort((a, b) => {
      if (!a.start || !b.start) return 0;
      if (a.start < b.start) return -1;
      if (a.start > b.start) return 1;
      return 0;
    })
    responses.forEach(x => x.index = responses.indexOf(x));
    this.SendIndexUpdates(responses, responses[0]?.eventId ?? "");
  }

  /** The selected picker day as a company calendar day key. */
  private selectedDayKey(): DayKey {
    return pickerDateToDayKey(this.selectedDay.date);
  }

  /**
   * The company calendar day a `?date=` deep link points at, or `undefined` when there is none (or it is junk).
   *
   * Accepts both shapes callers send: a plain `yyyy-MM-dd` day, taken as is, and an ISO INSTANT (what
   * `event-details` and the virtual-event tile send, `event.start.toISOString()`), whose company day is
   * resolved in `timeZone` — never `new Date(...).getDate()`, which would be the viewer's browser day.
   * An unparseable value degrades to "no deep link", i.e. the board opens on today.
   */
  private deepLinkDayKey(timeZone: string): DayKey | undefined {
    const raw = this.deepLinkDate;
    if (!raw) return undefined;
    if (isDayKey(raw)) return raw;

    const instant = new Date(raw);
    return isNaN(instant.getTime()) ? undefined : companyDayKey(instant, timeZone);
  }

  /**
   * A shift time as its `datetime-local` value: the COMPANY wall clock. These times become payroll hours, so a
   * shift at 22:00 Berlin must read 22:00 for an admin in New York too (`TimeUtilities.dateToString` used the
   * browser's clock).
   */
  shiftTimeValue(instant: Date | undefined): string {
    return toDateTimeLocalValue(instant, this.companyTimeZone.timeZone());
  }

  /**
   * A typed shift time, read as the COMPANY wall clock (a New York admin typing 22:00 for a Berlin company means
   * 22:00 Berlin). An empty value clears the time.
   */
  stringToDate(response: EmployeeEventResponseDto, string: string, key: Side) {
    this.setShiftTime(response, string == "" ? undefined : parseDateTimeLocalValue(string, this.companyTimeZone.timeZone()), key);
  }

  private setShiftTime(response: EmployeeEventResponseDto, instant: Date | undefined, key: Side) {
    let previous: EmployeeEventResponseDtoMinimal = new EmployeeEventResponseDtoMinimal(response);

    switch (key) {
      case Side.Left:
        response.start = instant
        break;
      case Side.Right:
        response.end = instant
        break;
    }

    this.SendChange(response, previous).then(r => this.applyPushResult(response, r));
  }

  setEventStartDate(response: EmployeeEventResponseDto) {
    let event = response.locationId.split("_")[1];
    let qEvent = this.events.find(x => x.id == event);
    if (qEvent == undefined) return;

    this.setShiftTime(response, new Date(qEvent.start), Side.Left);
  }

  setEventEndDate(response: EmployeeEventResponseDto) {
    let event = response.locationId.split("_")[1];
    let qEvent = this.events.find(x => x.id == event);
    if (qEvent == undefined) return;

    let now = new Date();

    if (qEvent.start <= now && qEvent.end > now) {
      // Round "now" to the configured segment on the COMPANY clock (matters for zones with a :30/:45 offset).
      const timeZone = this.companyTimeZone.timeZone();
      const wall = companyWallClock(now, timeZone);
      const segment = 60 / this.settings.segmentsPerHour;
      let factor = Math.floor(wall.minute / segment);
      let left = wall.minute % segment;
      if (left >= this.settings.segmentRoundingPoint) factor++;
      this.setShiftTime(response, companyLocalTimeInstant(wall.day, {hour: wall.hour, minute: factor * segment}, timeZone), Side.Right);
    } else {
      this.setShiftTime(response, new Date(qEvent.end), Side.Right);
    }
  }

  selectChanged(event: any) {
    let target = event.target as HTMLElement;
    target.parentElement!.parentElement!.parentElement!.attributes.getNamedItem("cdkDragDisabled")
  }

  employeesPerResponse(event: EventNl, state: ResponseState) {
    event.state = state;
    event.employeesPerResponse = this.employeeList.filter(
      x => (state == ResponseState.Declined ? event.deniedIds : state == ResponseState.Accepted ? event.acceptedIds : event.pendingIds).indexOf(x.id) > -1
    ).sort((a, b) => {
      if ((a.firstName + a.lastName).toLowerCase() < (b.firstName + b.lastName).toLowerCase()) return -1;
      if ((a.firstName + a.lastName).toLowerCase() > (b.firstName + b.lastName).toLowerCase()) return 1;
      return 0;
    })
  }

  closeEmployeesPerResponse(event: EventNl) {
    event.employeesPerResponse = undefined;
  }

  openPopup(e: any, employee: EmployeeNl, response: EmployeeEventResponseDto) {
    this.redirectPath = ['/' + AppRoutes.CompanyModule + "/" + CompanyRoutes.ShiftMaker]
    this.queryParams = {'event': response.locationId == "drop_base" ? response.eventId : response.locationId.split("_")[1]}
    this.queryParams['date'] = this.filteredEvents.find(x => x.id == this.queryParams['event'])?.start.toISOString();

    this.popupEmployee = employee;
    this.mouseEvent = e;

    this.eVisible = {}
    this.eVisible[this.queryParams["event"] ?? "drop_base"] = true;
    //this.visible = true;
  }

  getRelative(): boolean {
    return window.innerWidth >= 768;
  }

  /**
   * Ends an event's availability sign-up deadline ("Anmeldefrist").
   *
   * **Deliberately still on `EventHttpService`, and deliberately NOT moved onto the shift plan's own
   * controller (#409).** It is not a shift-plan write: it permanently closes sign-ups on the Event itself, with
   * effect long after the running window a `ShiftLead` is bounded to, and #280 gave that role only the
   * time-bounded `PushLiveUpdate`. Putting it on `ShiftPlanController` would either hand shift leads a
   * capability they were deliberately not given, or split that controller's single gate in two — and one gate
   * is the entire point of the controller. The button is already hidden for anyone without the legacy
   * `Inventory.Event.ChangePublicResponseTime` permission (see the template), so no session sees a control that
   * would 403.
   */
  lock(event: EventNl) {
    if (!confirm("Möchten Sie die Anmeldefrist wirklich beenden?")) return;

    this.eventHttpService.changePublicResponseTime(event.id, new Date(), event.shardId)
      .subscribe({
        next: value => {
          event.publicResponseEnd = value.publicResponseEnd;
          this.now = new Date();
        },
        error: err => {
          console.error(err);
          this.globalAlertService.createAndShow("Fehler", "Fehler beim Sperren des Events.", AlertLevel.error);
        }
      })
  }

}
